import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${(props) => props.zIndex || 9000};
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div`
  position: relative;
  z-index: ${(props) => props.zIndex + 1 || 9001};
`;

const disableScroll = () => {
  const scrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.width = '100%';
};

const enableScroll = () => {
  if (document.body.style.position === 'fixed') {
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  }
};

export const ModalV2 = ({
  isOpen,
  onClose,
  children,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  disableScrollLock = false,
  zIndex = 9000,
}) => {
  const [modalRoot, setModalRoot] = React.useState(null);

  useEffect(() => {
    setModalRoot(document.getElementById('modal-root') || document.body);
  }, []);

  // Handle scroll lock
  useEffect(() => {
    if (!disableScrollLock) {
      if (isOpen) {
        disableScroll();
      } else {
        enableScroll();
      }
    }

    return () => {
      if (!disableScrollLock) {
        enableScroll();
      }
    };
  }, [isOpen, disableScrollLock]);

  // Handle ESC key
  useEffect(() => {
    if (!closeOnEsc || !isOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [closeOnEsc, isOpen, onClose]);

  if (!modalRoot || !isOpen) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <Overlay isOpen={isOpen} onClick={handleOverlayClick} zIndex={zIndex}>
      <ModalContainer zIndex={zIndex}>{children}</ModalContainer>
    </Overlay>,
    modalRoot
  );
};

ModalV2.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  closeOnOverlayClick: PropTypes.bool,
  closeOnEsc: PropTypes.bool,
  disableScrollLock: PropTypes.bool,
  zIndex: PropTypes.number,
};

export default ModalV2;
